<template>
  <VueFinalModal
    v-model="opened"
    :click-to-close="true"
    :esc-to-close="true"
    :focus-trap="modalFocusTrap"
    class="final-modal"
    overlay-transition="vfm-fade"
    :class="{
      'theme--default': theme === 'light',
      'theme--dark': theme === 'dark',
    }"
    @closed="onClosed"
  >
    <div
      class="content"
      aria-modal="true"
      role="dialog"
      aria-labelledby="runAutomationTitle"
      tabindex="-1"
    >
      <div class="header">
        <h4 id="runAutomationTitle" class="title">Run automation</h4>
        <BaseButton kind="auxiliary" class="close-icon" @click="close">
          <template #icon>
            <CloseIcon />
          </template>
        </BaseButton>
      </div>

      <div class="body">
        <div v-if="status.type === 'needs-context'" class="hint">
          Open this from an object or a page to run an automation.
        </div>

        <BaseNoResults
          v-else-if="status.type === 'loading-org'"
          :is-loading="true"
          class="no-results"
        />

        <template v-else>
          <FormInput
            ref="inputRef"
            :value="query"
            type="text"
            placeholder="Filter automations…"
            @update:value="(v: string) => (query = v)"
            @keydown="onKeyDown"
          />

          <div
            ref="optionsEl"
            class="options"
            role="listbox"
            aria-label="Automations"
          >
            <BaseNoResults
              v-if="status.type === 'no-results'"
              :is-loading="false"
              class="no-results"
            />

            <template v-if="status.type === 'results'">
              <button
                v-for="opt in status.options"
                :key="optionKey(opt)"
                type="button"
                class="option"
                :class="{ 'option--selected': optionKey(opt) === selectedKey }"
                role="option"
                :aria-selected="optionKey(opt) === selectedKey"
                @click="selectedKey = optionKey(opt)"
              >
                <AutomationIcon class="option-icon" />
                <span class="option-name">{{ opt.automation.name }}</span>
                <span class="option-agent">{{ opt.agentName }}</span>
                <CheckmarkIcon
                  v-if="optionKey(opt) === selectedKey"
                  class="option-check"
                />
              </button>
            </template>
          </div>
        </template>
      </div>

      <div class="footer">
        <div class="buttons">
          <BaseButton
            kind="main"
            type="tertiary"
            text="Cancel"
            @click="close"
          />
          <BaseButton
            kind="main"
            type="primary"
            text="Run"
            :state="confirmState"
            @click="confirm"
          />
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";
import { VueFinalModal } from "vue-final-modal";

import type { AnyAgentId } from "@ctrl/controller-rpc";

import { isAssistant } from "@/components/ai/agents";
import type { Automation } from "@/components/ai/useAutomations";
import { ORPHAN_AGENT_KEY } from "@/components/ai/useAutomations";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseNoResults from "@/components/base/BaseNoResults.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import AutomationIcon from "@/components/icons/AutomationIcon.vue";
import CheckmarkIcon from "@/components/icons/CheckmarkIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import * as inject from "@/components/inject";
import { modalFocusTrap } from "@/components/modals/focus-trap";

type AutomationOption = {
  agentId: AnyAgentId | null;
  agentName: string;
  automation: Automation;
};

type Status =
  | { type: "needs-context" }
  | { type: "loading-org" }
  | { type: "no-results" }
  | { type: "results"; options: AutomationOption[] };

const optionKey = (opt: AutomationOption): string =>
  `${opt.agentId ?? "none"}:${opt.automation.id}`;

export default defineComponent({
  name: "ModalPalette",
  components: {
    AutomationIcon,
    BaseButton,
    BaseNoResults,
    CheckmarkIcon,
    CloseIcon,
    FormInput,
    VueFinalModal,
  },
  setup() {
    const palette = inject.palette();
    const theme = inject.theme();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const agents = inject.agents();

    const opened = ref(false);
    const inputRef = ref<InstanceType<typeof FormInput> | null>(null);
    const optionsEl = ref<HTMLElement | null>(null);
    const query = ref("");
    const selectedKey = ref<string | null>(null);

    const close = () => {
      opened.value = false;
    };

    const onClosed = () => {
      query.value = "";
      selectedKey.value = null;
      isInputFocused.value = false;
      isModalShown.value = false;
    };

    const open = () => {
      query.value = "";
      opened.value = true;
      isModalShown.value = true;
      void nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            inputRef.value?.focus();
            isInputFocused.value = true;
          });
        });
      });
    };

    onBeforeUnmount(() => {
      isInputFocused.value = false;
      isModalShown.value = false;
    });

    const allOptions = computed<AutomationOption[]>(() => {
      const ctx = palette.context.value;
      if (!ctx) return [];
      const orgId = ctx.organizationId;
      if (!orgId) return [];

      const automationsValue = palette.automations.value;
      const all = automationsValue?.all.value;
      if (!all) return [];

      // Build a map from agent ObjectIds to agent names
      const agentIdToName = new Map<string, string>();
      const activeAgents = agents.value?.active[orgId] ?? [];
      for (const agent of activeAgents) {
        agentIdToName.set(agent.id, agent.name ?? "Agent");
      }

      const opts: AutomationOption[] = [];
      // Automation ids are unique org-wide; dedupe in case the current org is
      // the personal org, where the assistant's automations already appear.
      const seenAutomationIds = new Set<string>();
      const pushOption = (agentId: string, automation: Automation) => {
        if (seenAutomationIds.has(automation.id)) return;
        seenAutomationIds.add(automation.id);
        const isOrphan = agentId === ORPHAN_AGENT_KEY;
        opts.push({
          agentId: isOrphan ? null : agentId,
          agentName: isOrphan
            ? "No agent"
            : isAssistant(agentId)
              ? "Assistant"
              : (agentIdToName.get(agentId) ?? "Unknown Agent"),
          automation,
        });
      };

      // All automations from the current organization (every agent + orphans)…
      for (const [agentId, byAutomation] of Object.entries(all[orgId] ?? {})) {
        for (const automation of Object.values(byAutomation)) {
          pushOption(agentId, automation);
        }
      }

      // …plus the assistant's automations, which live in the personal
      // organization and can operate on objects of any organization.
      const assistant = automationsValue?.assistant.value;
      if (assistant) {
        const byAutomation =
          all[assistant.organizationId]?.[assistant.agentId] ?? {};
        for (const automation of Object.values(byAutomation)) {
          pushOption(assistant.agentId, automation);
        }
      }

      opts.sort((a, b) => {
        const byAgent = a.agentName.localeCompare(b.agentName);
        if (byAgent !== 0) return byAgent;
        return a.automation.name.localeCompare(b.automation.name);
      });

      return opts;
    });

    const filteredOptions = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return allOptions.value;
      return allOptions.value.filter((o) => {
        return (
          o.automation.name.toLowerCase().includes(q) ||
          o.agentName.toLowerCase().includes(q)
        );
      });
    });

    // Keep a valid selection: default to the first visible option.
    watch(
      filteredOptions,
      (opts) => {
        const stillVisible = opts.some(
          (o) => optionKey(o) === selectedKey.value
        );
        if (!stillVisible) {
          selectedKey.value = opts.length > 0 ? optionKey(opts[0]) : null;
        }
      },
      { immediate: true }
    );

    watch(selectedKey, () => {
      void nextTick(() => {
        optionsEl.value
          ?.querySelector(".option--selected")
          ?.scrollIntoView({ block: "nearest" });
      });
    });

    const selectedOption = computed(
      () =>
        filteredOptions.value.find((o) => optionKey(o) === selectedKey.value) ??
        null
    );

    const status = computed<Status>(() => {
      const ctx = palette.context.value;
      if (!ctx) return { type: "needs-context" };
      if (!ctx.organizationId) return { type: "loading-org" };

      const opts = filteredOptions.value;
      if (opts.length === 0) return { type: "no-results" };
      return { type: "results", options: opts };
    });

    const confirmState = computed(() =>
      selectedOption.value ? "default" : "disabled"
    );

    const confirm = () => {
      const option = selectedOption.value;
      if (!option) return;
      close();
      // Trigger asynchronously, in the organization owning the automation:
      // assistant automations belong to the personal organization even when
      // run on an object from another organization.
      void palette.triggerAutomation(
        option.automation.organizationId,
        option.agentId,
        option.automation.id
      );
    };

    const moveSelection = (delta: number) => {
      const opts = filteredOptions.value;
      if (opts.length > 0) {
        const index = opts.findIndex((o) => optionKey(o) === selectedKey.value);
        const next =
          index === -1 ? 0 : (index + delta + opts.length) % opts.length;
        selectedKey.value = optionKey(opts[next]);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveSelection(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        moveSelection(-1);
      } else if (event.key === "Enter") {
        event.preventDefault();
        confirm();
      }
    };

    return {
      modalFocusTrap,
      theme,
      opened,
      inputRef,
      optionsEl,
      query,
      selectedKey,
      status,
      confirmState,
      optionKey,
      open,
      close,
      onClosed,
      onKeyDown,
      confirm,
    };
  },
});
</script>

<style scoped lang="scss">
.content {
  display: flex;
  flex-direction: column;
  border-radius: $border-radius-l;
  width: 560px;
  box-shadow: 0px 1px 32px 0px rgba(0, 0, 0, 0.12);

  @include themed() {
    background-color: t($bg);
    color: t($primary);
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;

  @include themed() {
    border-bottom: 1px solid t($grey-15);
  }
}

.title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.hint {
  padding: 8px 6px;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-50);
  }
}

.no-results {
  padding: 8px 0;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 320px;
  min-height: 120px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: $border-radius-m;
  background: none;
  text-align: start;

  @include themed() {
    color: t($primary);
  }

  &:hover {
    @include themed() {
      background: t($grey-05);
    }
  }

  &--selected,
  &--selected:hover {
    @include themed() {
      background: t($grey-10);
    }
  }
}

.option-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  :deep(.stroke) {
    @include themed() {
      stroke: t($grey-60);
    }
  }
}

.option-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @extend %font-caption-medium;
}

.option-agent {
  flex-shrink: 0;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-50);
  }
}

.option-check {
  flex-shrink: 0;
  width: 14px;
  height: 14px;

  :deep(.stroke) {
    @include themed() {
      stroke: t($tertiary-100);
    }
  }
}

.footer {
  padding: 12px 16px;

  @include themed() {
    border-top: 1px solid t($grey-10);
  }

  .buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
